import { Eye, Lock, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { StrongText, type StrongTextKey } from "@/components/strong-text";
import { Button } from "@/components/ui/button";
import { ViewAsSwitcher } from "@/components/view-as-switcher";
import { useStopViewAs, type ViewAsState } from "@/lib/api";
import { apiErrorMessage, isToastedError } from "@/lib/api-errors";
import { logger } from "@/lib/logger";
import { roleLabel } from "@/lib/roles";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { isViewAsRole, reloadAfterStop, VIEW_AS_LAYOUT, viewAsName } from "@/lib/view-as";

interface ViewAsBarProps {
  /** The view that is on, as `/auth/me` carries it. */
  view: ViewAsState;
}

/**
 * The bar above every page while a developer views the dashboard as someone
 * else: across the whole window, above the sidebar and the page, in De
 * Ceuster blue, 44 px high, and it never scrolls away (the shell keeps the
 * sidebar and the phone's top bar under it). Left, whom the dashboard shows
 * ("Je bekijkt het dashboard als Davy De Ceuster (Eigenaar)", or "als een
 * Lid zonder team" for a role); right, Wissel (another person or role),
 * "Alleen lezen" with a lock, and Stoppen, which ends the view and loads
 * Settings → Bekijken als, without the bar. A stop that fails says why in an
 * error toast, and the view stays on. Below 1024 px the sentence is shorter ("Als Davy De Ceuster
 * (Eigenaar)"); the name is what gets cut short. On a phone Wissel and Stoppen
 * show their icons alone, and "Alleen lezen" its lock.
 */
export function ViewAsBar({ view }: ViewAsBarProps) {
  const { t } = useTranslation();
  const stop = useStopViewAs();

  const onStop = () => {
    // Stopped: the page is about to load Settings → Bekijken als.
    if (stop.isPending || stop.isSuccess) {
      return;
    }
    logger.debug("[view-as]", "stop");
    stop.mutateAsync().then(reloadAfterStop, (error: unknown) => {
      logger.warn("[view-as] stop failed", error);
      if (!isToastedError(error)) {
        toast.error(() => apiErrorMessage(error, "stopViewAs"));
      }
    });
  };

  return (
    <section
      aria-label={t("viewAs.bar.label")}
      data-view-as-bar=""
      className={cn(
        "bg-primary text-primary-foreground sticky top-0 z-50 flex items-center gap-2 px-3 text-sm sm:gap-3 sm:px-4",
        VIEW_AS_LAYOUT.bar,
      )}
    >
      <Eye aria-hidden="true" className="size-4 shrink-0" />
      <ViewAsSentence view={view} short className="lg:hidden" />
      <ViewAsSentence view={view} className="max-lg:hidden" />
      <ViewAsSwitcher view={view} />
      <span className="flex shrink-0 items-center gap-1.5">
        <Lock aria-hidden="true" className="size-4" />
        <span className="max-sm:sr-only">{t("viewAs.bar.readOnly")}</span>
      </span>
      <Button
        type="button"
        variant="ghost"
        aria-label={t("viewAs.bar.stop")}
        aria-disabled={stop.isPending || stop.isSuccess || undefined}
        onClick={onStop}
        className={cn(
          VIEW_AS_LAYOUT.button,
          "bg-primary-foreground text-primary hover:bg-primary-foreground/90 hover:text-primary",
        )}
      >
        <X aria-hidden="true" className="size-4" />
        <span className="max-sm:hidden">{t("viewAs.bar.stop")}</span>
      </Button>
    </section>
  );
}

interface ViewAsSentenceProps {
  view: ViewAsState;
  /** "Als Davy De Ceuster (Eigenaar)" rather than the whole sentence. */
  short?: boolean;
  className?: string;
}

/**
 * Whom the dashboard shows, in one line with the name in bold. The name
 * shrinks first, to an ellipsis, so the words round it stay whole.
 */
function ViewAsSentence({ view, short = false, className }: ViewAsSentenceProps) {
  const role = roleLabel(view.role);
  let key: StrongTextKey;
  let values: Record<string, string>;
  if (view.kind === "role") {
    const viewed = isViewAsRole(view.role) ? view.role : "user";
    key = short ? `viewAs.bar.roleShort.${viewed}` : `viewAs.bar.role.${viewed}`;
    values = { role };
  } else {
    key = short ? "viewAs.bar.personShort" : "viewAs.bar.person";
    values = { name: viewAsName(view), role };
  }
  return (
    // Preformatted, so the spaces round the name stay; a flex row, so only the name shrinks.
    <p className={cn("flex min-w-0 flex-1 overflow-hidden whitespace-pre", className)}>
      <StrongText i18nKey={key} values={values} strongClassName="min-w-0 truncate font-semibold" />
    </p>
  );
}
